import { type ReactNode, useEffect } from 'react';
import { X } from 'lucide-react';
import { ModalPortal } from '../ModalPortal';

/**
 * Composant Modal partagé (US-TR-04) — encapsule le pattern récurrent :
 *
 *  - backdrop full-viewport via `ModalPortal` (bug fix pré-Phase 9 — sinon
 *    les marges héritées de `space-y-*` du parent décalent le backdrop) ;
 *  - header avec titre + bouton X ;
 *  - body scrollable indépendant ;
 *  - footer flex-shrink-0 pour les actions ;
 *  - fermeture par Échap, click backdrop ou bouton X.
 *
 * Migration douce : les modals existantes continuent de fonctionner avec
 * leur pattern actuel (HTML inline + `<ModalPortal>`). Toute nouvelle
 * modal doit utiliser `<Modal>` pour éviter la duplication.
 */

const WIDTH_CLASSES = {
  sm: 'max-w-sm',
  md: 'max-w-md',
  lg: 'max-w-lg',
  xl: 'max-w-xl',
  '2xl': 'max-w-2xl',
  '3xl': 'max-w-3xl',
  '4xl': 'max-w-4xl',
  '5xl': 'max-w-5xl',
} as const;

export type ModalWidth = keyof typeof WIDTH_CLASSES;

export interface ModalProps {
  /** Titre affiché dans le header (laisser vide pour pas de header). */
  title?: ReactNode;
  /** Icône optionnelle à gauche du titre. */
  titleIcon?: ReactNode;
  /** Largeur max (sm → 5xl). Défaut : `2xl`. */
  width?: ModalWidth;
  /** Empêche la fermeture par Échap / click backdrop (utile pendant un submit). */
  locked?: boolean;
  /** Cacher le bouton X (gardé visible par défaut). */
  hideCloseButton?: boolean;
  /** Permet de passer un `aria-label` au bouton fermer (défaut "Fermer"). */
  closeAriaLabel?: string;
  /** Permet d'augmenter le z-index pour les modals empilés. */
  zIndex?: 'z-50' | 'z-[60]' | 'z-[70]';
  /** Contenu du body. Reçoit son propre wrapper scrollable. */
  children: ReactNode;
  /** Pied de page (boutons d'action). Optionnel. */
  footer?: ReactNode;
  /** Callback de fermeture. */
  onClose(): void;
}

export function Modal({
  title,
  titleIcon,
  width = '2xl',
  locked = false,
  hideCloseButton = false,
  closeAriaLabel = 'Fermer',
  zIndex = 'z-50',
  children,
  footer,
  onClose,
}: ModalProps) {
  useEffect(() => {
    if (locked) return;
    function handleKey(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose();
    }
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [locked, onClose]);

  function handleBackdropClick(e: React.MouseEvent<HTMLDivElement>) {
    if (locked) return;
    if (e.target === e.currentTarget) onClose();
  }

  return (
    <ModalPortal>
      <div
        className={`fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center ${zIndex} p-4`}
        onClick={handleBackdropClick}
        role="dialog"
        aria-modal="true"
      >
        <div
          className={`bg-white rounded-xl ${WIDTH_CLASSES[width]} w-full shadow-2xl max-h-[90vh] flex flex-col`}
        >
          {(title !== undefined || !hideCloseButton) && (
            <div className="flex items-center justify-between p-4 border-b border-gray-200 flex-shrink-0">
              <h2 className="text-lg font-semibold text-gray-900 flex items-center min-w-0">
                {titleIcon ? <span className="mr-2 flex-shrink-0">{titleIcon}</span> : null}
                <span className="truncate">{title}</span>
              </h2>
              {!hideCloseButton && (
                <button
                  type="button"
                  onClick={onClose}
                  className="p-1 rounded-lg hover:bg-gray-100 disabled:opacity-50"
                  aria-label={closeAriaLabel}
                  disabled={locked}
                >
                  <X className="w-5 h-5 text-gray-500" />
                </button>
              )}
            </div>
          )}

          <div className="flex-1 overflow-y-auto p-4">{children}</div>

          {footer && (
            <div className="flex items-center justify-end space-x-2 p-4 border-t border-gray-200 flex-shrink-0">
              {footer}
            </div>
          )}
        </div>
      </div>
    </ModalPortal>
  );
}
